/**
 * Canonical radiology domain constants shared across frontend components.
 *
 * Derived from the CPT exam dataset (config/radiology_llm_exam_dataset_1200.json).
 * Must stay in sync with backend enum values where applicable.
 */

export const MODALITIES = [
  'Angiography',
  'CT',
  'Fluoroscopy',
  'MRI',
  'Nuclear Medicine',
  'Ultrasound',
  'X-ray',
] as const;

export type Modality = (typeof MODALITIES)[number];

export const BODY_REGIONS = [
  'Abdomen',
  'Ankle',
  'Aorta',
  'Brain',
  'Breast',
  'Carotid',
  'Cervical Spine',
  'Chest',
  'Elbow',
  'Face',
  'Foot',
  'Hand',
  'Head',
  'Heart',
  'Hip',
  'Kidney',
  'Knee',
  'Liver',
  'Lumbar Spine',
  'Lung',
  'Neck',
  'Orbit',
  'Pancreas',
  'Pelvis',
  'Prostate',
  'Shoulder',
  'Thoracic Spine',
  'Thyroid',
  'Uterus',
  'Wrist',
] as const;

export type BodyRegion = (typeof BODY_REGIONS)[number];

export const LATERALITY_OPTIONS = [
  { value: 'left', label: 'Left' },
  { value: 'right', label: 'Right' },
  { value: 'bilateral', label: 'Bilateral' },
] as const;

export type Laterality = (typeof LATERALITY_OPTIONS)[number]['value'];

export const STUDY_PRIORITIES = ['ROUTINE', 'URGENT', 'STAT'] as const;

export type StudyPriority = (typeof STUDY_PRIORITIES)[number];

export const GENDER_OPTIONS = [
  { value: 'M', label: 'Male' },
  { value: 'F', label: 'Female' },
  { value: 'O', label: 'Other' },
] as const;

export type Gender = (typeof GENDER_OPTIONS)[number]['value'];

/** Resolve a raw gender code to a display label. */
export const genderLabel = (code: string | null | undefined): string => {
  const match = GENDER_OPTIONS.find((g) => g.value === code);
  return match ? match.label : (code ?? '—');
};
